/**
 * SamplingDetailsDialog — READ-ONLY "Sample Details" view, converted 1:1 from
 * Access `frmQualityQrySamplingAddInfo` (opened read-only by id). Mirrors the
 * grid/title/actions style of DeliveryOrderFormDialog but every field is a
 * disabled label/value (no Save). 2-col layout (Access Left 3312 → col 1,
 * 9792 → col 2), row order by Top, with two section headers ("Lot's
 * Information" / "Sample Sending Information"). Number fields (Standard) are
 * right-aligned with 2 decimals; dates render DD-MMM-YYYY.
 *
 * Field order (Access Top):
 *   sample_no | ps_date
 *   tlc_contract_ref | do_no
 *   — Lot's Information —
 *   warehouse_code | client_lot_id
 *   lot_id | weight_per_cont_mt (num)
 *   lot_size_mt (num) | cert_unit
 *   license_cert_no | grade
 *   origin |
 *   — Sample Sending Information —
 *   qty_preshipment_client (num) | ps_sent_date (date)
 *   cost (num) | qty_preshipment_supervisor (num)
 *   qty_aftershipment_client (num) | qty_aftershipment_supervisor (num)
 *   receiver | seal_no
 *   inspection_by | receiving_authority
 *   note (span 2)
 */
import {
  Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle,
  Stack, TextField, Typography,
} from '@mui/material'
import { type SxProps, type Theme } from '@mui/material/styles'

import { type QualitySampling } from '../../store/api/qualitySamplingApi'
import { useGetQualitySamplingByIdQuery } from '../../store/api/samplingDetailsApi'
import { formatDate } from '../../utils/date'

interface Props {
  open: boolean
  samplingId: number | null
  onClose: () => void
}

const compactInputSx: SxProps<Theme> = {
  '& .MuiInputBase-input': { py: 0.5, fontSize: '0.8125rem' },
  '& .MuiInputLabel-root': { fontSize: '0.8125rem', fontWeight: 500 },
  '& .MuiInputLabel-shrink': { fontWeight: 600 },
}
const titleBtnSx: SxProps<Theme> = {
  minWidth: 0, px: 1.25, py: 0.3, fontSize: '0.78rem', textTransform: 'none',
}
const sectionTitleSx: SxProps<Theme> = {
  fontWeight: 700, fontSize: '0.8125rem', color: 'primary.main',
  mt: 1.5, mb: 0.75, pb: 0.25, borderBottom: '1px solid', borderColor: 'divider',
  gridColumn: '1 / -1',
}
// Read-only field — distinct bg so it never reads as editable.
const readonlyFieldSx = {
  '& .MuiInputBase-root': {
    bgcolor: (t: Theme) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#f1f3f5'),
  },
  '& .MuiInputBase-input': {
    color: (t: Theme) => t.palette.text.secondary,
    fontWeight: 600,
  },
}

function formatStandard(v: number | null | undefined): string {
  if (v === null || v === undefined) return ''
  return v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

export default function SamplingDetailsDialog({ open, samplingId, onClose }: Props) {
  const { data, isFetching } = useGetQualitySamplingByIdQuery(samplingId as number, {
    skip: !open || samplingId == null,
  })

  const sampleNo = data?.sampleNo ?? ''

  // Generic read-only text field (string value, left-aligned).
  const txt = (
    key: keyof QualitySampling,
    label: string,
    opts: { sx?: SxProps<Theme>; multiline?: boolean; rows?: number } = {},
  ) => (
    <TextField
      label={label}
      size="small"
      fullWidth
      value={(data?.[key] as string | number | null | undefined) ?? ''}
      multiline={opts.multiline}
      rows={opts.multiline ? (opts.rows ?? 3) : undefined}
      InputProps={{ readOnly: true }}
      InputLabelProps={{ shrink: true }}
      sx={{ ...readonlyFieldSx, ...opts.sx }}
    />
  )

  // Read-only number field — Standard format, right-aligned.
  const num = (key: keyof QualitySampling, label: string, sx?: SxProps<Theme>) => (
    <TextField
      label={label}
      size="small"
      fullWidth
      value={formatStandard(data?.[key] as number | null | undefined)}
      InputProps={{ readOnly: true }}
      InputLabelProps={{ shrink: true }}
      inputProps={{ style: { textAlign: 'right' } }}
      sx={{ ...readonlyFieldSx, ...sx }}
    />
  )

  // Read-only date field — DD-MMM-YYYY.
  const date = (key: keyof QualitySampling, label: string, sx?: SxProps<Theme>) => (
    <TextField
      label={label}
      size="small"
      fullWidth
      value={formatDate(data?.[key] as string | null | undefined)}
      InputProps={{ readOnly: true }}
      InputLabelProps={{ shrink: true }}
      sx={{ ...readonlyFieldSx, ...sx }}
    />
  )

  const col1: SxProps<Theme> = { gridColumn: { xs: 1, sm: 1 } }
  const col2: SxProps<Theme> = { gridColumn: { xs: 1, sm: 2 } }

  return (
    <Dialog
      open={open}
      maxWidth="lg"
      fullWidth
      onClose={(_e, reason) => { if (reason === 'backdropClick') return; onClose() }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <Typography component="span" variant="subtitle1" sx={{ fontWeight: 700 }}>
          Sample Details{sampleNo ? ` - ${sampleNo}` : ''}
        </Typography>
        <Stack direction="row" spacing={0.75} sx={{ ml: 'auto' }}>
          <Button type="button" variant="outlined" sx={titleBtnSx} onClick={onClose}>Close</Button>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={compactInputSx}>
        {isFetching ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
              columnGap: 1.5,
              rowGap: 1.25,
              alignItems: 'start',
            }}
          >
            {/* Sample - Info */}
            <Box sx={col1}>{txt('sampleNo', 'Sample No')}</Box>
            <Box sx={col2}>{date('psDate', 'PS Date')}</Box>
            <Box sx={col1}>{txt('tlcContractRef', 'TLC Contract Ref')}</Box>
            <Box sx={col2}>{txt('doNo', 'DO No')}</Box>

            <Typography component="span" sx={sectionTitleSx}>Lot&apos;s Information</Typography>

            <Box sx={col1}>{txt('warehouseCode', 'Warehouse Code')}</Box>
            <Box sx={col2}>{txt('clientLotId', 'Client Lot ID')}</Box>
            <Box sx={col1}>{txt('lotId', 'Lot ID')}</Box>
            <Box sx={col2}>{num('weightPerContMt', 'Weight Per Cont Mt')}</Box>
            <Box sx={col1}>{num('lotSizeMt', 'Lot Size Mt')}</Box>
            <Box sx={col2}>{txt('certUnit', 'Cert Unit')}</Box>
            <Box sx={col1}>{txt('licenseCertNo', 'License Cert No')}</Box>
            <Box sx={col2}>{txt('grade', 'Grade')}</Box>
            <Box sx={col1}>{txt('origin', 'Origin')}</Box>

            <Typography component="span" sx={sectionTitleSx}>Sample Sending Information</Typography>

            <Box sx={col1}>{num('qtyPreshipmentClient', 'Qty Preshipment Client')}</Box>
            <Box sx={col2}>{date('psSentDate', 'PS Sent Date')}</Box>
            <Box sx={col1}>{num('cost', 'Cost')}</Box>
            <Box sx={col2}>{num('qtyPreshipmentSupervisor', 'Qty Preshipment Supervisor')}</Box>
            <Box sx={col1}>{num('qtyAftershipmentClient', 'Qty Aftershipment Client')}</Box>
            <Box sx={col2}>{num('qtyAftershipmentSupervisor', 'Qty Aftershipment Supervisor')}</Box>
            <Box sx={col1}>{txt('receiver', 'Receiver')}</Box>
            <Box sx={col2}>{txt('sealNo', 'Seal No')}</Box>
            <Box sx={col1}>{txt('inspectionBy', 'Inspection By')}</Box>
            <Box sx={col2}>{txt('receivingAuthority', 'Receiving Authority')}</Box>
            <Box sx={{ gridColumn: '1 / -1' }}>{txt('note', 'Note', { multiline: true, rows: 3 })}</Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  )
}
